import {
  type ExchangeGroup,
  ExchangeGroupZodSchema,
} from "@redotech/redo-return-core-common/exchange-group";
import {
  ExchangesSelection,
  ShopSiteNavigationMode,
} from "@redotech/redo-return-core-common/exchange-settings";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type {
  PortalSettingsPatch,
  ReturnsPortalSettingsSource,
  StoredExchangeGroup,
} from "./portal-settings-source";

export const exchangeUiSettingsShape = {
  exchangeUi: z.object({
    selection: createEnum(ExchangesSelection),
    checkoutSelector: z.string(),
    shopSiteURL: z.string(),
    shopSiteNewTab: z.boolean(),
    showFullPriceOnly: z.boolean(),
    bannerColor: z.string(),
    bannerFontColor: z.string(),
    bannerFont: z.string(),
    bannerText: z.string(),
    bannerShowDoneButton: z.boolean(),
    bannerPositionBottom: z.boolean(),
    bannerInsertBeforeBody: z.boolean(),
    bannerCustomCss: z.string(),
    /** Read by the shop-now catalog to tint variant swatches. */
    variantPickerColorHexMetafield: z.string(),
    exchangeGroups: z.array(ExchangeGroupZodSchema),
  }),
};

export type ExchangeUiSettings = z.infer<
  z.ZodObject<typeof exchangeUiSettingsShape>
>;

/** Only the fields its kind uses; a stored group missing one its kind needs is dropped. */
function exchangeGroupFields(
  group: StoredExchangeGroup,
): ExchangeGroup | undefined {
  const base = {
    _id: group._id,
    groupName: group.groupName,
    priceDifference: group.priceDifference,
  };
  const { sourceName, sourceCollection, targetCollection } = group;
  switch (group.type) {
    case "tag:tag":
      return {
        ...base,
        type: group.type,
        sourceTags: group.sourceTags ?? [],
        targetTags: group.targetTags ?? [],
      };
    case "tag:collection":
      return targetCollection
        ? {
            ...base,
            type: group.type,
            sourceTags: group.sourceTags ?? [],
            targetCollection,
          }
        : undefined;
    case "name:collection":
      return targetCollection && sourceName
        ? { ...base, type: group.type, sourceName, targetCollection }
        : undefined;
    case "collection:collection":
      return sourceCollection && targetCollection
        ? { ...base, type: group.type, sourceCollection, targetCollection }
        : undefined;
  }
}

export function exchangeUiSettingsFromTeam({
  settings,
}: ReturnsPortalSettingsSource): ExchangeUiSettings {
  const { exchanges } = settings;
  return {
    exchangeUi: {
      selection: exchanges.selection ?? ExchangesSelection.RETURN_APP,
      checkoutSelector: settings.checkoutSelector ?? "",
      shopSiteURL: exchanges.shopSiteURL ?? "",
      shopSiteNewTab:
        exchanges.shopSite?.navigationMode === ShopSiteNavigationMode.NEW_TAB,
      showFullPriceOnly:
        exchanges.exchangeOptionsPreviewShowOriginalPrice ?? false,
      bannerColor: settings.exchangeBannerColor ?? "",
      bannerFontColor: settings.exchangeBannerFontColor ?? "",
      bannerFont: settings.exchangeBannerFont ?? "",
      bannerText: settings.exchangeBannerText ?? "",
      bannerShowDoneButton: settings.exchangeBannerShowDoneButton ?? true,
      bannerPositionBottom:
        settings.shopifyExtension?.exchangeBannerPositionBottom ?? false,
      bannerInsertBeforeBody:
        settings.shopifyExtension?.exchangeBannerInsertBeforeBody ?? false,
      bannerCustomCss: settings.exchangeBannerCustomCss ?? "",
      variantPickerColorHexMetafield:
        exchanges.variantPickerColorHexMetafield ?? "",
      exchangeGroups: (settings.exchangeGroups ?? []).flatMap((group) => {
        const fields = exchangeGroupFields(group);
        return fields ? [fields] : [];
      }),
    },
  };
}

export function exchangeUiTeamPatch({
  exchangeUi,
}: ExchangeUiSettings): PortalSettingsPatch {
  const set: Record<string, unknown> = {
    "exchanges.selection": exchangeUi.selection,
    "exchanges.shopSiteURL": exchangeUi.shopSiteURL,
    "exchanges.shopSite.navigationMode": exchangeUi.shopSiteNewTab
      ? ShopSiteNavigationMode.NEW_TAB
      : ShopSiteNavigationMode.SAME_TAB,
    "exchanges.exchangeOptionsPreviewShowOriginalPrice":
      exchangeUi.showFullPriceOnly,
    exchangeBannerColor: exchangeUi.bannerColor,
    exchangeBannerFontColor: exchangeUi.bannerFontColor,
    exchangeBannerFont: exchangeUi.bannerFont,
    exchangeBannerText: exchangeUi.bannerText,
    exchangeBannerShowDoneButton: exchangeUi.bannerShowDoneButton,
    exchangeBannerCustomCss: exchangeUi.bannerCustomCss,
    "shopifyExtension.exchangeBannerPositionBottom":
      exchangeUi.bannerPositionBottom,
    "shopifyExtension.exchangeBannerInsertBeforeBody":
      exchangeUi.bannerInsertBeforeBody,
    exchangeGroups: exchangeUi.exchangeGroups,
  };
  const unset: string[] = [];

  // Blank means "no override", which the portal reads as an absent field, not
  // an empty string.
  const blankIsUnset: [string, string][] = [
    ["checkoutSelector", exchangeUi.checkoutSelector],
    [
      "exchanges.variantPickerColorHexMetafield",
      exchangeUi.variantPickerColorHexMetafield,
    ],
  ];
  for (const [path, value] of blankIsUnset) {
    const trimmed = value.trim();
    if (trimmed) {
      set[path] = trimmed;
    } else {
      unset.push(path);
    }
  }

  return { set, unset };
}
